import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { DatasetResponse, FetchRowsResult, Sort } from "../types";
import { JSONObject } from "@spring-global/spring-datasources/lib/src/Types";
import * as storage from "../../../context/Storage";

export const BASE_URL = (process.env.REACT_APP_API_BASE_URL ?? "").replace(/\/$/, "");

export interface JobID {
  _id?: string;
  publicId?: string;
}

export interface DataHubDatasetRequestParams extends JobID {
  top?: number;
  skip?: number;
  sortColumn?: string;
  sortDirection?: Sort["direction"];
  filtersState?: Record<string, unknown>;
  fetchDefinition?: boolean;
  fetchFiltersData?: string;
  export?: number;
  _requestId?: string;
}

export type ExportFormat = "csv" | "xlsx";

export interface ExportColumn {
  field: string;
  headerName: string;
}

export interface DataHubExportRequestParams extends JobID {
  sortColumn?: string;
  sortDirection?: Sort["direction"];
  filtersState?: Record<string, unknown>;
  format: ExportFormat;
  columns: ExportColumn[];
  /** Screen title used as the base of the downloaded file name. */
  fileName?: string;
}

interface DataHubObjectDetailRequestParams extends JobID {
  idObject: number | string;
  /** Busts the portal cache so Refresh also refreshes an already-viewed detail. */
  _requestId?: string;
}

interface DataHubGetAsyncFilterDataParams extends JobID {
  _requestId?: string;
  args?: {
    search?: string;
    top?: number;
    skip?: number;
  };
}

interface DataHubObjectDetailResponse {
  data: JSONObject;
  schema: JSONObject;
  uiSchema: JSONObject;
}

export class DataHubClient {
  private static postRequest<T>(
    url: string,
    params: JSONObject,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T, any, {}>> {
    const token = storage.local.getItem("accessToken");

    return axios.post<T>(BASE_URL + url, params, {
      ...config,
      headers: {
        ...config?.headers,
        Authorization: `Bearer ${(token || "").trim()}`,
      },
    });
  }

  public static async getDataset(
    params: DataHubDatasetRequestParams,
    config?: AxiosRequestConfig,
  ): Promise<DatasetResponse> {
    params.filtersState = Object.fromEntries(
      Object.entries(params.filtersState ?? {}).filter(
        ([, value]) =>
          value !== undefined &&
          value !== "" &&
          (!Array.isArray(value) || value.length > 0),
      ),
    );
    if (Object.keys(params.filtersState).length === 0) {
      delete params.filtersState;
    }
    const res = await DataHubClient.postRequest<DatasetResponse>(
      "/api/dataset",
      params,
      config,
    );
    return res.data;
  }

  public static async getAsyncFilterData(
    params: DataHubGetAsyncFilterDataParams,
    config?: AxiosRequestConfig,
  ): Promise<Omit<FetchRowsResult, "total">> {
    const res = await DataHubClient.postRequest<Omit<FetchRowsResult, "total">>(
      "/api/filter-data",
      params,
      config,
    );
    return res.data;
  }

  public static async getObjectDetail(
    params: DataHubObjectDetailRequestParams,
    config?: AxiosRequestConfig,
  ): Promise<DataHubObjectDetailResponse> {
    const res = await DataHubClient.postRequest<DataHubObjectDetailResponse>(
      "/api/data",
      params,
      config,
    );
    return {
      data: res.data.data,
      schema: res.data.schema,
      uiSchema: res.data.uiSchema,
    };
  }

  public static async exportDataset(
    params: DataHubExportRequestParams,
    signal?: AbortSignal,
  ): Promise<Response> {
    const token = storage.local.getItem("accessToken");
    const sanitizedFilters = Object.fromEntries(
      Object.entries(params.filtersState ?? {}).filter(
        ([, value]) =>
          value !== undefined &&
          value !== "" &&
          (!Array.isArray(value) || value.length > 0),
      ),
    );

    return fetch(BASE_URL + "/api/dataset/export", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${(token || "").trim()}`,
      },
      body: JSON.stringify({
        ...params,
        filtersState:
          Object.keys(sanitizedFilters).length > 0
            ? sanitizedFilters
            : undefined,
      }),
      signal,
    });
  }
}
